import { useCallback, useMemo } from 'react';
import { matchPath, useLocation, useParams } from 'react-router-dom';

import { MenuItem } from '@widgets/sidebar-menu-items';

import { UseBreadcrumbsOutput, UseBreadcrumbsProps } from './types';

export const useBreadcrumbs = ({
  navigation,
}: UseBreadcrumbsProps): UseBreadcrumbsOutput => {
  const location = useLocation();
  const params = useParams();

  const getBreadCrumbsItems = useCallback(
    (rootItem: MenuItem): MenuItem[] => {
      const currentRootItems =
        rootItem?.children?.filter((item) => {
          return matchPath(
            { path: `${item?.url}/*`, end: true },
            location.pathname
          );
        }) || [];

      const currentRootItem = currentRootItems[0];

      let result;
      if (currentRootItem?.children) {
        result = currentRootItem.children.reduce(
          (processedItems, childItem) => {
            const match = matchPath(
              { path: `${childItem?.url}*` ?? '' },
              location.pathname
            );
            const { paramsKey } = childItem;
            const processedChildItem = childItem.paramsKey
              ? {
                  ...childItem,
                  title: paramsKey ? params[paramsKey] : paramsKey,
                }
              : childItem;
            let currentItems = match ? [processedChildItem] : [];

            if (childItem.children) {
              currentItems = [
                ...currentItems,
                ...getBreadCrumbsItems(childItem),
              ];
            }

            return [...processedItems, ...currentItems];
          },
          [currentRootItem]
        );
      } else {
        const paramsKey = currentRootItem?.paramsKey;
        const processedRootItem = paramsKey
          ? {
              ...currentRootItem,
              title: params[paramsKey],
            }
          : currentRootItem;
        result = processedRootItem ? [processedRootItem] : [];
      }

      return result;
    },
    [location.pathname, params]
  );

  const items = useMemo(() => navigation?.items ?? [], [navigation]);

  const [rootItem] = items.filter((item) =>
    location.pathname.includes(item?.url ?? '')
  );

  return getBreadCrumbsItems(rootItem as MenuItem);
};
